import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Carousel } from 'react-responsive-carousel';
import { Helmet } from 'react-helmet';
import { FaTimes, FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import SocialShare from '../components/SocialShare';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Sample gallery images - in a real app, these would be actual images
  const galleryImages = useMemo(() => [
    {
      id: 1,
      src: 'https://images.unsplash.com/photo-1523050854058-8df901a735ad?w=800&h=600&fit=crop',
      alt: 'Students in science lab conducting experiments',
      title: 'Science Lab Activities',
      category: 'Academic',
      date: 'March 2024'
    },
    {
      id: 2,
      src: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=800&h=600&fit=crop',
      alt: 'Students performing on stage during drama festival',
      title: 'Drama Festival Performance',
      category: 'Arts',
      date: 'April 2024'
    },
    {
      id: 3,
      src: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=600&fit=crop',
      alt: 'Students playing sports on the school grounds',
      title: 'Sports Day Activities',
      category: 'Sports',
      date: 'May 2024'
    },
    {
      id: 4,
      src: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=800&h=600&fit=crop',
      alt: 'Students working on art projects in the art room',
      title: 'Art Class Creations',
      category: 'Arts',
      date: 'February 2024'
    },
    {
      id: 5,
      src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop',
      alt: 'Students collaborating on group projects',
      title: 'Group Project Collaboration',
      category: 'Academic',
      date: 'January 2024'
    },
    {
      id: 6,
      src: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?w=800&h=600&fit=crop',
      alt: 'Students reading in the school library',
      title: 'Library Study Session',
      category: 'Academic',
      date: 'March 2024'
    },
    {
      id: 7,
      src: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&h=600&fit=crop',
      alt: 'Students using computers in the computer lab',
      title: 'Computer Lab Session',
      category: 'Technology',
      date: 'February 2024'
    },
    {
      id: 8,
      src: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=800&h=600&fit=crop',
      alt: 'Students participating in music class',
      title: 'Music Class Performance',
      category: 'Arts',
      date: 'April 2024'
    },
    {
      id: 9,
      src: 'https://images.unsplash.com/photo-1509062522246-3755977927d7?w=800&h=600&fit=crop',
      alt: 'Students in the school cafeteria during lunch',
      title: 'Cafeteria Lunch Time',
      category: 'General',
      date: 'March 2024'
    }
  ], []);

  // Carousel images for the hero section
  const carouselImages = useMemo(() => [
    {
      src: 'https://images.unsplash.com/photo-1523050854058-8df901a735ad?w=1200&h=600&fit=crop',
      alt: 'Students conducting science experiments',
      title: 'Science Fair 2024',
      description: 'Students showcase their innovative projects and discoveries'
    },
    {
      src: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&h=600&fit=crop',
      alt: 'Drama festival performance on stage',
      title: 'Spring Drama Festival',
      description: 'A celebration of performing arts featuring student productions'
    },
    {
      src: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1200&h=600&fit=crop',
      alt: 'Students participating in sports activities',
      title: 'Annual Sports Day',
      description: 'Promoting fitness and teamwork through athletic competition'
    },
    {
      src: 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=1200&h=600&fit=crop',
      alt: 'Students creating art projects',
      title: 'Art Exhibition',
      description: 'Showcasing creative talents and artistic expression'
    }
  ], []);

  const openLightbox = useCallback((image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
    setIsLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    setSelectedImage(null);
  }, []);

  const nextImage = useCallback(() => {
    const nextIndex = (currentImageIndex + 1) % galleryImages.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(galleryImages[nextIndex]);
  }, [currentImageIndex, galleryImages]);

  const prevImage = useCallback(() => {
    const prevIndex = currentImageIndex === 0 ? galleryImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(galleryImages[prevIndex]);
  }, [currentImageIndex, galleryImages]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeLightbox();
    } else if (e.key === 'ArrowRight') {
      nextImage();
    } else if (e.key === 'ArrowLeft') {
      prevImage();
    }
  }, [closeLightbox, nextImage, prevImage]);

  useEffect(() => {
    if (isLightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen, currentImageIndex, handleKeyDown]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <Helmet>
        <title>Rasoha Academy | Photo Gallery - School Life & Events</title>
        <meta name="description" content="Explore Rasoha Academy's photo gallery showcasing school life, events, activities, and memorable moments. View our vibrant community and student achievements." />
        <meta name="keywords" content="Rasoha Academy Gallery, School Photos, Student Life, School Events, Narok School Images, Academic Activities" />
        <meta property="og:title" content="Rasoha Academy | Photo Gallery - School Life & Events" />
        <meta property="og:description" content="Explore Rasoha Academy's photo gallery showcasing school life, events, activities, and memorable moments." />
        <meta property="og:url" content="https://rasohaacademy.com/gallery" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Photo Gallery
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-90">
            Explore moments from our school life, events, and activities
          </p>
        </div>
      </section>

      {/* Image Carousel Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              School Highlights
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Featured moments from our school events and activities
            </p>
          </div>

          <div className="relative" data-aos="zoom-in">
            <Carousel
              showArrows={true}
              showThumbs={false}
              showStatus={false}
              showIndicators={true}
              infiniteLoop={true}
              autoPlay={true}
              interval={5000}
              stopOnHover={true}
              swipeable={true}
              emulateTouch={true}
              className="rounded-xl overflow-hidden shadow-2xl"
            >
              {carouselImages.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-64 md:h-96 object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white">
                    <h3 className="text-xl md:text-2xl font-bold mb-2">{image.title}</h3>
                    <p className="text-sm md:text-base opacity-90">{image.description}</p>
                  </div>
                </div>
              ))}
            </Carousel>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Photo Gallery
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto mb-6">
              Browse through our collection of school memories and moments
            </p>
            <div className="flex justify-center">
              <SocialShare 
                title="Rasoha Academy Photo Gallery"
                description="Explore amazing moments from our school life and activities"
                url="https://rasohaacademy.com/gallery"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((image, index) => (
              <div
                key={image.id}
                className="group relative bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer"
                data-aos="zoom-in"
                data-aos-delay={index * 100}
                onClick={() => openLightbox(image, index)}
              >
                <div className="relative">
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <FaExpand className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {image.title}
                  </h3>
                  <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
                    <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-full text-xs">
                      {image.category}
                    </span>
                    <span>{image.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Share Your Memories
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Have photos from school events? Share them with our community.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105">
              Submit Photos
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105">
              View Archive
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {isLightboxOpen && selectedImage && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-4xl max-h-full">
            {/* Close Button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors duration-200"
            >
              <FaTimes className="w-5 h-5" />
            </button>

            {/* Navigation Buttons */}
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors duration-200"
            >
              <FaChevronLeft className="w-5 h-5" />
            </button>

            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center hover:bg-black/70 transition-colors duration-200"
            >
              <FaChevronRight className="w-5 h-5" />
            </button>

            {/* Image */}
            <img
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            {/* Image Info */}
            <div className="absolute bottom-4 left-4 right-4 bg-black/50 text-white p-4 rounded-lg">
              <h3 className="text-xl font-bold mb-2">{selectedImage.title}</h3>
              <div className="flex items-center justify-between text-sm">
                <span className="bg-blue-600 px-2 py-1 rounded-full">
                  {selectedImage.category}
                </span>
                <span>{selectedImage.date}</span>
              </div>
            </div>

            {/* Image Counter */}
            <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {currentImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery; 